import { useState, useEffect, useCallback, useRef } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer, Label,
} from "recharts";
import SimulacionEscenarios, { SimulacionParams } from "./SimulacionEscenarios";
import { clp as fmt } from "../utils/format";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const pct  = n => (n ?? 0).toFixed(1) + "%";
const authFetch = url => fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// ── Helpers de UI ─────────────────────────────────────────────────────────────
function Badge({ label, value, color, bg }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", background: bg || "#fafaf9",
      border:`0.5px solid ${color}30`, borderRadius:10, padding:"0.65rem 1rem", minWidth:90, flex:1 }}>
      <div style={{ fontSize:18, fontWeight:700, color }}>{value}</div>
      <div style={{ fontSize:11, color:"#888", marginTop:2, textAlign:"center" }}>{label}</div>
    </div>
  );
}

function FilaDesglose({ label, formula, value, color, bold, sub }) {
  return (
    <div style={{ borderTop: bold ? "0.5px solid #eee" : "none" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start",
        padding:"7px 0", gap:12 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, color: bold ? "#111" : "#555", fontWeight: bold ? 600 : 400 }}>{label}</div>
          {formula && <div style={{ fontSize:10, color:"#ccc", marginTop:1 }}>{formula}</div>}
        </div>
        <span style={{ fontSize:13, fontWeight: bold ? 700 : 500, color, whiteSpace:"nowrap" }}>{value}</span>
      </div>
      {sub && (
        <div style={{ paddingLeft:12, paddingBottom:4, fontSize:11, color:"#bbb", fontStyle:"italic" }}>{sub}</div>
      )}
    </div>
  );
}

// ── Flujo de caja por producto ────────────────────────────────────────────────
function FlujoCajaProducto({ p }) {
  const isMobile = useIsMobile();
  // Margen vendidos: consistente con definición de Pulso (sin incluir fletes fallidos)
  const margenVendidos = (p.venta_total ?? 0) - (p.costo_proveedor ?? 0) - (p.flete_entregados ?? 0);
  const gastos         = (p.gasto_meta  ?? 0) + (p.flete_fallidos  ?? 0);
  const margenReal     = margenVendidos - gastos; // equivale a p.margen

  const pasos = [
    {
      label: "Venta",
      icon: "💰",
      value: fmt(p.venta_total ?? 0),
      sub: `${p.entregados ?? 0} entregados`,
      color: "#4338CA",
    },
    {
      label: "Margen vendidos",
      icon: "📦",
      value: fmt(margenVendidos),
      sub: "Venta − prov − flete ent.",
      color: margenVendidos >= 0 ? "#059669" : "#DC2626",
    },
    {
      label: "Gastos",
      icon: "📉",
      value: fmt(gastos),
      sub: "Meta + fletes fallidos",
      color: "#DC2626",
    },
    {
      label: "Margen real",
      icon: "💵",
      value: fmt(margenReal),
      sub: p.gasto_meta > 0 ? `ROAS ${(p.roas ?? 0).toFixed(2)}x` : "Sin Meta Ads",
      color: margenReal >= 0 ? "#059669" : "#DC2626",
    },
  ];

  return (
    <div style={{
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      background: "#FAFBFF",
      border: "1px solid #E4E6F5",
      borderRadius: 10,
      overflow: "hidden",
      marginBottom: 4,
    }}>
      {pasos.map((paso, i) => (
        <div key={paso.label} style={{ flex: 1, display: "flex", alignItems: "stretch" }}>
          <div style={{
            flex: 1,
            padding: isMobile ? "10px 14px" : "12px 6px",
            textAlign: isMobile ? "left" : "center",
            borderLeft: !isMobile && i > 0 ? "1px solid #E4E6F5" : "none",
            borderTop: isMobile && i > 0 ? "1px solid #E4E6F5" : "none",
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            justifyContent: isMobile ? "space-between" : "center",
            alignItems: isMobile ? "center" : "initial",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ fontSize: 13 }}>{paso.icon}</div>
              <div style={{
                fontSize: 9, color: "#9496B4", textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}>
                {paso.label}
              </div>
            </div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13, fontWeight: 700, color: paso.color,
            }}>
              {paso.value}
            </div>
          </div>
          {!isMobile && i < pasos.length - 1 && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 14, flexShrink: 0, color: "#CBD5E1", fontSize: 11,
              userSelect: "none",
            }}>
              →
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BarraEstado({ producto: p }) {
  const total = p.total_pedidos || 1;
  const segs = [
    { label:"Entregados",  v: p.entregados,  color:"#1D9E75" },
    { label:"En tránsito", v: p.en_transito, color:"#534AB7" },
    { label:"Pendientes",  v: p.pendientes,  color:"#EF9F27" },
    { label:"Cancelados",  v: p.cancelados,  color:"#aaa"    },
    { label:"Fallidos",    v: p.fallidos,    color:"#E24B4A" },
  ];
  return (
    <div>
      <div style={{ display:"flex", borderRadius:6, overflow:"hidden", height:10, marginBottom:"0.6rem" }}>
        {segs.map(s => (
          <div key={s.label} style={{ width:`${(s.v/total)*100}%`, background:s.color, transition:"width 0.3s" }} />
        ))}
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:"0.4rem 1rem" }}>
        {segs.map(s => (
          <div key={s.label} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:"#555" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:s.color }} />
            <span style={{ fontWeight:600 }}>{s.v}</span>
            <span style={{ color:"#aaa" }}>({pct((s.v/total)*100)})</span>
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Defaults de parámetros de escenarios ─────────────────────────────────────
const PARAMS_KEY     = "panel_esc_params";
const DEFAULT_PARAMS = { deltaAcid: 10, deltaOpt: 10, factorOpt: 0.8 };

function loadParams() {
  try { return { ...DEFAULT_PARAMS, ...JSON.parse(localStorage.getItem(PARAMS_KEY)) }; }
  catch { return DEFAULT_PARAMS; }
}

// ── Tarjeta por producto ──────────────────────────────────────────────────────
function ProductoCard({ p, escParams }) {
  const margenColor = (p.margen ?? 0) >= 0 ? "#1D9E75" : "#E24B4A";
  const margenBg    = (p.margen ?? 0) >= 0 ? "#f4fbf8"  : "#FCEBEB";

  return (
    <div style={{ background:"#fff", border:"0.5px solid #e8e8e6", borderRadius:14, padding:"1.25rem", display:"flex", flexDirection:"column", gap:"1.1rem" }}>

      {/* Flujo de caja ejecutivo */}
      <FlujoCajaProducto p={p} />

      {/* Nombre + estado campaña + tasa entrega */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:600, color:"#111", marginBottom:5 }}>{p.nombre}</div>
          <div style={{
            display:"inline-flex", alignItems:"center", gap:5,
            fontSize:11, fontWeight:500, padding:"3px 10px", borderRadius:20,
            background: p.campaña_activa ? "#f4fbf8" : "#f5f5f5",
            border: `0.5px solid ${p.campaña_activa ? "#1D9E7540" : "#ddd"}`,
            color: p.campaña_activa ? "#1D9E75" : "#999",
          }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background: p.campaña_activa ? "#1D9E75" : "#bbb" }} />
            {p.campaña_activa ? "Campaña activa" : "Campaña inactiva"}
          </div>
        </div>
        <div style={{ textAlign:"right", flexShrink:0 }}>
          <div style={{ fontSize:18, fontWeight:700, color: p.tasa_entrega >= 70 ? "#1D9E75" : p.tasa_entrega >= 50 ? "#EF9F27" : "#E24B4A" }}>
            {pct(p.tasa_entrega ?? 0)}
          </div>
          <div style={{ fontSize:10, color:"#aaa" }}>tasa entrega</div>
        </div>
      </div>

      {/* Badges de estado */}
      <div style={{ display:"flex", gap:"0.5rem", flexWrap:"wrap" }}>
        <Badge label="Entregados"  value={p.entregados}  color="#1D9E75" />
        <Badge label="En tránsito" value={p.en_transito} color="#534AB7" />
        <Badge label="Pendientes"  value={p.pendientes}  color="#EF9F27" />
        <Badge label="Cancelados"  value={p.cancelados}  color="#aaa"    />
        <Badge label="Fallidos"    value={p.fallidos}    color="#E24B4A" />
        <Badge
          label="Valor de venta"
          value={p.precio_venta > 0 ? fmt(p.precio_venta) : "—"}
          color="#4338CA"
          bg="#F0F0FF"
        />
      </div>

      {/* Barra de estados */}
      <BarraEstado producto={p} />

      {/* Desglose financiero */}
      <div style={{ background:"#fafaf9", border:"0.5px solid #eee", borderRadius:10, padding:"0.9rem 1rem" }}>
        <div style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:"0.5rem" }}>Desglose financiero</div>

        <FilaDesglose label="+ Venta total (entregados)"
          formula={`${p.entregados} pedidos · ESTATUS = ENTREGADO`}
          value={fmt(p.venta_total)} color="#1D9E75" />

        <FilaDesglose label="− Costo proveedor"
          formula="PRECIO PROVEEDOR X CANTIDAD · ESTATUS = ENTREGADO"
          value={`−${fmt(p.costo_proveedor)}`} color="#E24B4A" />

        <FilaDesglose label="− Flete entregados"
          formula="PRECIO FLETE · ESTATUS = ENTREGADO"
          value={`−${fmt(p.flete_entregados)}`} color="#E24B4A" />

        <FilaDesglose label="− Flete fallidos"
          formula="PRECIO FLETE · ESTATUS = DEVOLUCION/FALLIDO"
          value={`−${fmt(p.flete_fallidos)}`} color="#E24B4A" />

        <FilaDesglose label="= Margen Productos Vendidos" bold
          formula="Venta − Costo proveedor − Flete entregados − Flete fallidos"
          value={fmt(p.margen_prod_vend)} color={(p.margen_prod_vend ?? 0) >= 0 ? "#1D9E75" : "#E24B4A"} />

        <div style={{ height:6 }} />

        <FilaDesglose label="− Gasto Meta Ads"
          formula="Proporcional al total de pedidos del producto"
          value={`−${fmt(p.gasto_meta)}`} color="#E24B4A"
          sub={p.gasto_meta > 0 ? `ROAS ${(p.roas ?? 0).toFixed(2)}x · CPA ${fmt(p.cpa_real)}` : null} />

        <FilaDesglose label="= Margen neto" bold
          formula="Margen Productos Vendidos − Gasto Meta"
          value={fmt(p.margen)} color={margenColor} />

        {/* Indicador visual margen */}
        <div style={{ background:margenBg, border:`0.5px solid ${margenColor}20`, borderRadius:8,
          padding:"0.6rem 0.85rem", marginTop:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:12, color:margenColor, fontWeight:600 }}>Margen neto</span>
          <span style={{ fontSize:16, fontWeight:700, color:margenColor }}>{fmt(p.margen)}</span>
        </div>
      </div>

      {/* Warning si falta gasto Meta */}
      {(p.gasto_meta === 0 || p.gasto_meta == null) && (
        <div style={{ background:"#FFF8EC", border:"0.5px solid #EF9F2730", borderRadius:10, padding:"0.9rem 1.1rem" }}>
          <div style={{ fontSize:12, fontWeight:600, color:"#7A4F00", marginBottom:4 }}>⚠️ Sin datos de Meta Ads</div>
          <div style={{ fontSize:13, color:"#555", lineHeight:1.5 }}>
            No hay gasto registrado en pauta para este producto en el período. El margen mostrado no descuenta inversión publicitaria — el resultado real puede ser menor.
          </div>
        </div>
      )}

      {/* Simulación de escenarios (incluye recomendación unificada) */}
      <SimulacionEscenarios p={p} params={escParams} />
    </div>
  );
}

// ── Mapa de Rentabilidad ──────────────────────────────────────────────────────
const CUADRANTES = [
  { id:"ok",    label:"Rentable",                desc:"Margen positivo con y sin Meta",      color:"#1D9E75", bg:"#f4fbf8" },
  { id:"meta",  label:"Meta lo hundió",          desc:"Producto rentable, Meta lo consume",   color:"#EF9F27", bg:"#FFF8EC" },
  { id:"bad",   label:"No rentable",             desc:"Margen negativo en ambos niveles",     color:"#E24B4A", bg:"#FCEBEB" },
  { id:"weird", label:"Margen neto sin producto",desc:"Margen neto positivo sin base sólida", color:"#534AB7", bg:"#F0EEFF" },
];

function cuadrante(p) {
  const mpv = p.margen_prod_vend ?? 0;
  const mn  = p.margen ?? 0;
  if (mpv >= 0 && mn >= 0) return "ok";
  if (mpv >= 0 && mn < 0)  return "meta";
  if (mpv < 0  && mn < 0)  return "bad";
  return "weird";
}

// ── Mapa de Audiencia (Factor de inflación vs NDR) ──────────────────────────
// Factor de inflación = total pedidos / confirmados. Mide calidad de audiencia/tráfico.
// NDR = (confirmados − entregados) / confirmados. Mide calidad del producto / gestión.
const factorInflacion = (p) => {
  const tc = p.tasa_confirmacion ?? 0;
  return tc > 0 ? 100 / tc : null;
};
const ndrPorcentaje = (p) => Math.max(0, 100 - (p.tasa_entrega ?? 0));

const UMBRAL_FACTOR = 1.20;
const UMBRAL_NDR    = 30;

const CUADRANTES_AUD = [
  { id:"escalar",   label:"Escalar",       desc:"Audiencia limpia + entrega buena. Acá poné el presupuesto.",  color:"#1D9E75", bg:"#f4fbf8" },
  { id:"trampa",    label:"Trampa",        desc:"Entrega bien pero la audiencia infla los costos. Meta te cobra de más.", color:"#EF9F27", bg:"#FFF8EC" },
  { id:"entrega",   label:"Falla entrega", desc:"Audiencia limpia pero el producto no llega. Revisar transportadoras o calidad.", color:"#534AB7", bg:"#F0EEFF" },
  { id:"descartar", label:"Descartar",     desc:"Audiencia sucia + entrega mala. Pausar y rearmar.", color:"#E24B4A", bg:"#FCEBEB" },
];

function cuadranteAudiencia(p) {
  const f = factorInflacion(p);
  const ndr = ndrPorcentaje(p);
  if (f == null) return "descartar";
  const audSucia    = f >= UMBRAL_FACTOR;
  const entregaMala = ndr >= UMBRAL_NDR;
  if (!audSucia && !entregaMala) return "escalar";
  if ( audSucia && !entregaMala) return "trampa";
  if (!audSucia &&  entregaMala) return "entrega";
  return "descartar";
}

const DIM_CONFIG = [
  { key: "margenNeto",     label: "Margen neto",     locked: true,  desc: "Eje Y del gráfico" },
  { key: "margenProdVend", label: "Margen vendidos",  locked: false, desc: "Agrega eje X real" },
  { key: "volumen",        label: "Volumen pedidos",  locked: false, desc: "Varía el tamaño" },
];

function TooltipMapa({ active, payload, dims, modo }) {
  if (!active || !payload?.length) return null;
  const p   = payload[0].payload;
  const esAud = modo === "audiencia";
  const cq  = esAud
    ? CUADRANTES_AUD.find(c => c.id === cuadranteAudiencia(p))
    : CUADRANTES.find(c => c.id === cuadrante(p));
  const f   = factorInflacion(p);
  const ndr = ndrPorcentaje(p);
  return (
    <div style={{ background:"#fff", border:"0.5px solid #e8e8e6", borderRadius:10,
      padding:"0.75rem 1rem", fontSize:12, boxShadow:"0 4px 16px #0001", maxWidth:260 }}>
      <div style={{ fontWeight:600, color:"#111", marginBottom:4, lineHeight:1.3 }}>{p.nombre}</div>
      <div style={{ display:"inline-block", fontSize:10, fontWeight:600, color:cq.color,
        background:cq.bg, borderRadius:6, padding:"2px 8px", marginBottom:8 }}>{cq.label}</div>

      {esAud ? (
        <>
          <div style={{ color:"#888", marginBottom:2 }}>
            Factor de inflación: <span style={{ color: (f ?? 0) < UMBRAL_FACTOR ? "#1D9E75" : "#EF9F27", fontWeight:600 }}>
              {f != null ? f.toFixed(2) + "x" : "—"}</span>
          </div>
          <div style={{ color:"#888", marginBottom:2 }}>
            NDR: <span style={{ color: ndr < UMBRAL_NDR ? "#1D9E75" : "#E24B4A", fontWeight:600 }}>
              {ndr.toFixed(1)}%</span>
          </div>
          <div style={{ color:"#888", marginBottom:2, fontSize:11 }}>
            TC {pct(p.tasa_confirmacion ?? 0)} · TE {pct(p.tasa_entrega ?? 0)}
          </div>
          <div style={{ color:"#888", marginBottom:2 }}>
            Pedidos: <span style={{ color:"#111", fontWeight:500 }}>{p.total_pedidos ?? 0}</span>
          </div>
        </>
      ) : (
        <>
          <div style={{ color:"#888", marginBottom:2 }}>
            Margen neto: <span style={{ color: (p.margen??0)>=0?"#1D9E75":"#E24B4A", fontWeight:600 }}>
              {fmt(p.margen)}</span>
          </div>
          {dims.margenProdVend && (
            <div style={{ color:"#888", marginBottom:2 }}>
              Margen vendidos: <span style={{ color: (p.margen_prod_vend??0)>=0?"#1D9E75":"#E24B4A", fontWeight:600 }}>
                {fmt(p.margen_prod_vend)}</span>
            </div>
          )}
          {dims.volumen && (
            <div style={{ color:"#888", marginBottom:2 }}>
              Pedidos: <span style={{ color:"#111", fontWeight:500 }}>{p.total_pedidos ?? 0}</span>
            </div>
          )}
          {(p.gasto_meta > 0) && (
            <div style={{ color:"#888", marginBottom:2 }}>
              Meta: <span style={{ color:"#111", fontWeight:500 }}>{fmt(p.gasto_meta)}</span>
              {" · "}ROAS <span style={{ color:"#111", fontWeight:500 }}>{(p.roas??0).toFixed(2)}x</span>
            </div>
          )}
        </>
      )}
      <div style={{ fontSize:10, color:"#bbb", marginTop:6 }}>Click para ver detalle</div>
    </div>
  );
}

function MapaProductos({ datos, onSelect }) {
  const [modo, setModo] = useState("rentabilidad"); // "rentabilidad" | "audiencia"
  const [dims, setDims] = useState({ margenNeto: true, margenProdVend: false, volumen: false });

  const toggleDim = key => setDims(d => ({ ...d, [key]: !d[key] }));
  const esAud = modo === "audiencia";

  // ── Modo rentabilidad: ordenamiento + dominio ─────────────────────────────
  const sortedByMn = [...datos].sort((a, b) => (a.margen ?? 0) - (b.margen ?? 0));
  const indexMap = {};
  sortedByMn.forEach((p, i) => { indexMap[p.nombre] = i; });

  // ── Puntos según modo ────────────────────────────────────────────────────
  const puntos = datos.map(p => {
    if (esAud) {
      return { ...p, x: factorInflacion(p) ?? 1, y: ndrPorcentaje(p) };
    }
    return {
      ...p,
      x: dims.margenProdVend ? (p.margen_prod_vend ?? 0) : indexMap[p.nombre],
      y: p.margen ?? 0,
    };
  });

  const allX = puntos.map(p => p.x);
  const allY = puntos.map(p => p.y);

  // ── Dominio ejes ─────────────────────────────────────────────────────────
  let xDomain, yDomain, fmtAxisX, fmtAxisY, refLineX, refLineY, xAxisLabel, yAxisLabel, yAxisWidth;

  if (esAud) {
    const maxF = Math.max(2, ...allX.filter(v => Number.isFinite(v)));
    xDomain = [0.95, Math.min(maxF * 1.08, 4)]; // clamp para evitar outliers gigantes
    yDomain = [-2, Math.max(60, Math.min(100, Math.max(...allY) * 1.1))];
    fmtAxisX = v => v.toFixed(2) + "x";
    fmtAxisY = v => v.toFixed(0) + "%";
    refLineX = UMBRAL_FACTOR;
    refLineY = UMBRAL_NDR;
    xAxisLabel = "← Limpia · Factor inflación · Inflada →";
    yAxisLabel = "NDR (%)";
    yAxisWidth = 45;
  } else {
    xDomain = dims.margenProdVend
      ? (() => {
          const minX = Math.min(0, ...allX); const maxX = Math.max(0, ...allX);
          const pad = (maxX - minX) * 0.18 || 500000;
          return [minX - pad, maxX + pad];
        })()
      : [-0.5, datos.length - 0.5];
    const minY = Math.min(0, ...allY); const maxY = Math.max(0, ...allY);
    const padY = (maxY - minY) * 0.18 || 500000;
    yDomain = [minY - padY, maxY + padY];
    fmtAxisX = v => {
      if (!dims.margenProdVend) return "";
      return Math.abs(v) >= 1000000
        ? (v/1000000).toFixed(1)+"M"
        : Math.abs(v) >= 1000 ? Math.round(v/1000)+"k" : v;
    };
    fmtAxisY = v => Math.abs(v) >= 1000000
      ? (v/1000000).toFixed(1)+"M"
      : Math.abs(v) >= 1000 ? Math.round(v/1000)+"k" : v;
    refLineX = dims.margenProdVend ? 0 : null;
    refLineY = 0;
    xAxisLabel = dims.margenProdVend
      ? "← Pérdida sin Meta · Margen Productos Vendidos · Ganancia sin Meta →"
      : "← Menor margen neto · Productos ordenados · Mayor margen neto →";
    yAxisLabel = "Margen Neto";
    yAxisWidth = 55;
  }

  const CustomDot = ({ cx, cy, payload }) => {
    const cq    = esAud ? cuadranteAudiencia(payload) : cuadrante(payload);
    const arr   = esAud ? CUADRANTES_AUD : CUADRANTES;
    const color = arr.find(c => c.id === cq)?.color ?? "#aaa";
    const r     = !esAud && dims.volumen
      ? 7 + Math.min(18, (payload.total_pedidos ?? 0) / 8)
      : 9;
    return (
      <g style={{ cursor:"pointer" }} onClick={() => onSelect(payload.nombre)}>
        <circle cx={cx} cy={cy} r={r + 3} fill={color} fillOpacity={0.1} />
        <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={payload.campaña_activa ? 0.9 : 0.35}
          stroke="#fff" strokeWidth={1.5} />
      </g>
    );
  };

  // ── Resumen por cuadrante ─────────────────────────────────────────────────
  const cuadrantesArr = esAud ? CUADRANTES_AUD : CUADRANTES;
  const getCuadrante  = esAud ? cuadranteAudiencia : cuadrante;
  const resumen = cuadrantesArr.map(cq => ({
    ...cq, n: puntos.filter(p => getCuadrante(p) === cq.id).length,
  })).filter(cq => cq.n > 0);

  const tituloMapa    = esAud ? "Mapa de audiencia" : "Mapa de rentabilidad";
  const subtituloMapa = esAud
    ? `Factor < ${UMBRAL_FACTOR}x sano · NDR < ${UMBRAL_NDR}% sano`
    : `${dims.margenProdVend ? "Eje X: Margen Vendidos · " : "Ranking por margen neto · "}Eje Y: Margen Neto${dims.volumen ? " · Tamaño: volumen" : " · Tamaño: fijo"}`;

  // ── Toggle de modo ────────────────────────────────────────────────────────
  const ModoToggle = () => (
    <div style={{ display:"inline-flex", background:"#F4F5FA", borderRadius:10, padding:3, gap:2 }}>
      {[
        { key:"rentabilidad", label:"Rentabilidad" },
        { key:"audiencia",    label:"Audiencia" },
      ].map(({ key, label }) => {
        const active = modo === key;
        return (
          <button
            key={key}
            onClick={() => setModo(key)}
            style={{
              fontSize:11, fontWeight:700, padding:"5px 14px", borderRadius:8,
              border:"none", cursor:"pointer",
              background: active ? "#fff" : "transparent",
              color: active ? "#059669" : "#6B7280",
              boxShadow: active ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
              transition:"all 0.15s",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ background:"#fff", border:"0.5px solid #e8e8e6", borderRadius:12, padding:"1.25rem" }}>

      {/* Header: título + toggle modo + dimensiones */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"0.9rem", gap:16, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:240 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6, flexWrap:"wrap" }}>
            <div style={{ fontSize:13, fontWeight:600, color:"#111" }}>{tituloMapa}</div>
            <ModoToggle />
          </div>
          <div style={{ fontSize:11, color:"#aaa" }}>{subtituloMapa}</div>
          {/* Resumen cuadrantes */}
          <div style={{ display:"flex", gap:"0.5rem", flexWrap:"wrap", marginTop:8 }}>
            {resumen.map(cq => (
              <div key={cq.id} title={cq.desc} style={{ background:cq.bg, border:`0.5px solid ${cq.color}30`,
                borderRadius:8, padding:"0.3rem 0.75rem", display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:7, height:7, borderRadius:"50%", background:cq.color }} />
                <span style={{ fontSize:12, fontWeight:600, color:cq.color }}>{cq.n}</span>
                <span style={{ fontSize:11, color:"#666" }}>{cq.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Controles de dimensiones (solo modo rentabilidad) */}
        {!esAud && (
          <div style={{ flexShrink:0, display:"flex", flexDirection:"column", gap:6, alignItems:"flex-end" }}>
            <div style={{ fontSize:10, color:"#aaa", marginBottom:2, fontWeight:500 }}>
              DIMENSIONES VISIBLES
            </div>
            {DIM_CONFIG.map(({ key, label, locked, desc }) => {
              const active = dims[key];
              return (
                <button
                  key={key}
                  disabled={locked}
                  onClick={() => !locked && toggleDim(key)}
                  title={desc}
                  style={{
                    fontSize:11, fontWeight:600, padding:"5px 12px", borderRadius:20,
                    border:`1.5px solid ${active ? "#059669" : "#E4E6F5"}`,
                    background: active ? "#ECFDF5" : "#FAFBFF",
                    color: active ? "#059669" : locked ? "#9496B4" : "#6B7280",
                    cursor: locked ? "default" : "pointer",
                    display:"flex", alignItems:"center", gap:7,
                    transition:"all 0.15s",
                  }}
                >
                  <div style={{
                    width:7, height:7, borderRadius:"50%",
                    background: active ? "#059669" : "#ccc",
                    flexShrink:0,
                  }} />
                  {label}
                  {locked && (
                    <span style={{ fontSize:9, color:"#9496B4", fontWeight:400 }}>siempre</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ position:"relative" }}>
      {/* Etiquetas de cuadrante (solo modo audiencia) */}
      {esAud && (
        <>
          <div style={{ position:"absolute", top:14, left:70, fontSize:10, fontWeight:700, color:"#534AB7", opacity:0.55, pointerEvents:"none", letterSpacing:"0.04em", textTransform:"uppercase" }}>Falla entrega</div>
          <div style={{ position:"absolute", top:14, right:28, fontSize:10, fontWeight:700, color:"#E24B4A", opacity:0.55, pointerEvents:"none", letterSpacing:"0.04em", textTransform:"uppercase" }}>Descartar</div>
          <div style={{ position:"absolute", bottom:62, left:70, fontSize:10, fontWeight:700, color:"#1D9E75", opacity:0.7, pointerEvents:"none", letterSpacing:"0.04em", textTransform:"uppercase" }}>Escalar</div>
          <div style={{ position:"absolute", bottom:62, right:28, fontSize:10, fontWeight:700, color:"#EF9F27", opacity:0.65, pointerEvents:"none", letterSpacing:"0.04em", textTransform:"uppercase" }}>Trampa</div>
        </>
      )}
      <ResponsiveContainer width="100%" height={340}>
        <ScatterChart margin={{ top:10, right:20, bottom:40, left:20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />

          {/* Líneas de referencia: thresholds (audiencia) o ceros (rentabilidad) */}
          {refLineX != null && (
            <ReferenceLine x={refLineX} stroke={esAud ? "#EF9F2780" : "#E24B4A40"} strokeWidth={1.5} strokeDasharray={esAud ? "4 4" : undefined} />
          )}
          {refLineY != null && (
            <ReferenceLine y={refLineY} stroke={esAud ? "#EF9F2780" : "#E24B4A40"} strokeWidth={1.5} strokeDasharray={esAud ? "4 4" : undefined} />
          )}

          <XAxis
            type="number"
            dataKey="x"
            domain={xDomain}
            tickFormatter={fmtAxisX}
            tick={(esAud || dims.margenProdVend) ? { fontSize:10, fill:"#bbb" } : false}
          >
            <Label
              value={xAxisLabel}
              position="insideBottom"
              offset={-24}
              fontSize={9}
              fill="#aaa"
            />
          </XAxis>

          <YAxis
            type="number"
            dataKey="y"
            domain={yDomain}
            tickFormatter={fmtAxisY}
            tick={{ fontSize:10, fill:"#bbb" }}
            width={yAxisWidth}
          >
            <Label value={yAxisLabel} angle={-90} position="insideLeft" offset={12} fontSize={10} fill="#aaa" />
          </YAxis>

          <Tooltip content={<TooltipMapa dims={dims} modo={modo} />} cursor={{ strokeDasharray:"3 3" }} />
          <Scatter data={puntos} shape={<CustomDot />} />
        </ScatterChart>
      </ResponsiveContainer>
      </div>

      {/* Leyenda de tamaño (solo cuando volumen activo y modo rentabilidad) */}
      {!esAud && dims.volumen && (
        <div style={{ display:"flex", alignItems:"center", gap:"1.25rem", marginTop:8,
          justifyContent:"center", flexWrap:"wrap" }}>
          <span style={{ fontSize:10, color:"#bbb" }}>Tamaño = volumen de pedidos:</span>
          {[{ pedidos:15, label:"~15" }, { pedidos:60, label:"~60" }, { pedidos:130, label:"130+" }].map(({ pedidos, label }) => {
            const r = 7 + Math.min(18, pedidos / 8);
            const box = (r + 3) * 2 + 2;
            return (
              <div key={pedidos} style={{ display:"flex", alignItems:"center", gap:6 }}>
                <svg width={box} height={box} style={{ overflow:"visible" }}>
                  <circle cx={box/2} cy={box/2} r={r} fill="#888" fillOpacity={0.22}
                    stroke="#aaa" strokeWidth={1} />
                </svg>
                <span style={{ fontSize:10, color:"#999" }}>{label} pedidos</span>
              </div>
            );
          })}
          <span style={{ fontSize:10, color:"#bbb" }}>· Opacidad baja = campaña inactiva</span>
        </div>
      )}

      {!esAud && !dims.volumen && (
        <div style={{ fontSize:10, color:"#bbb", textAlign:"center", marginTop:8 }}>
          Haz click en un producto para ver su detalle · Activá "Volumen pedidos" para variar el tamaño de los puntos
        </div>
      )}

      {esAud && (
        <div style={{ fontSize:10, color:"#bbb", textAlign:"center", marginTop:8 }}>
          Líneas punteadas = umbrales sanos (factor {UMBRAL_FACTOR}x · NDR {UMBRAL_NDR}%). Opacidad baja = campaña inactiva. Click en un producto para ver su detalle.
        </div>
      )}
    </div>
  );
}

// ── Tooltip "sin Meta Ads" ────────────────────────────────────────────────────
function TooltipSinMeta({ n }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position:"relative", display:"inline-flex", alignItems:"center", gap:4, marginLeft:8 }}>
      <span style={{ color:"#EF9F27", fontWeight:500 }}>⚠️ {n} sin Meta Ads</span>
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        style={{ fontSize:9, color:"#bbb", cursor:"help", border:"0.5px solid #ddd",
          borderRadius:"50%", width:13, height:13, display:"inline-flex",
          alignItems:"center", justifyContent:"center", fontWeight:600, lineHeight:1 }}>?</span>
      {visible && (
        <div style={{ position:"absolute", bottom:"calc(100% + 6px)", left:"50%",
          transform:"translateX(-50%)", background:"#222", color:"#fff",
          fontSize:10, borderRadius:7, padding:"7px 11px", zIndex:100,
          boxShadow:"0 4px 12px #0003", lineHeight:1.6, width:230, whiteSpace:"normal" }}>
          <div style={{ fontWeight:600, marginBottom:3 }}>¿Qué significa esto?</div>
          Estos productos no tienen gasto registrado en Meta Ads en el período. Pueden ser orgánicos (venden sin pauta) o productos que aún no se han activado en campaña.
          <div style={{ marginTop:5, color:"#ffcc80" }}>
            Sin Meta, la visibilidad depende 100% del alcance orgánico de Dropi, lo que limita el volumen de pedidos potencial.
          </div>
        </div>
      )}
    </span>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function DetalleProducto({ desde, hasta }) {
  const [datos,    setDatos]    = useState([]);
  const [cargando, setCargando] = useState(false);
  // Inicializar búsqueda desde sessionStorage si Alertas dejó un producto pre-seleccionado.
  const [busqueda, setBusqueda] = useState(() => {
    try {
      const filtro = sessionStorage.getItem("producto_filtro_inicial");
      if (filtro) {
        sessionStorage.removeItem("producto_filtro_inicial");
        return filtro;
      }
    } catch {}
    return "";
  });
  const [escParams, setEscParams] = useState(loadParams);
  const [escAbierto, setEscAbierto] = useState(false);
  const cardRefs = useRef({});

  const handleEscParams = p => {
    setEscParams(p);
    localStorage.setItem(PARAMS_KEY, JSON.stringify(p));
  };

  const fetchDatos = useCallback(async (d, h) => {
    if (!d || !h) return;
    setCargando(true);
    try {
      const res = await authFetch(`${API_URL}/api/productos-detalle?desde=${d}&hasta=${h}`);
      if (res.ok) setDatos(await res.json());
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { fetchDatos(desde, hasta); }, [desde, hasta, fetchDatos]);

  const filtrados = datos.filter(p =>
    !busqueda.trim() || p.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  // Totales sobre los productos visibles
  const totalVenta     = filtrados.reduce((s, p) => s + (p.venta_total      ?? 0), 0);
  const totalCostoProv = filtrados.reduce((s, p) => s + (p.costo_proveedor  ?? 0), 0);
  const totalFleteEnt  = filtrados.reduce((s, p) => s + (p.flete_entregados ?? 0), 0);
  const totalFleteFall = filtrados.reduce((s, p) => s + (p.flete_fallidos   ?? 0), 0);
  const margenProdVend = filtrados.reduce((s, p) => s + (p.margen_prod_vend ?? 0), 0);
  const sinMeta        = filtrados.filter(p => !p.gasto_meta).length;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1.25rem" }}>

      {/* Buscador */}
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <input
          type="text"
          placeholder="Buscar producto..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ flex:1, fontSize:13, padding:"8px 14px", borderRadius:10,
            border:"0.5px solid #ddd", outline:"none", background:"#fff" }}
        />
        {cargando && (
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#aaa" }}>
            <div style={{ width:14, height:14, border:"2px solid #eee", borderTop:"2px solid #1D9E75",
              borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
            Cargando...
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {!cargando && (
          <span style={{ fontSize:12, color:"#aaa", whiteSpace:"nowrap" }}>
            {filtrados.length} producto{filtrados.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Mapa de productos */}
      {datos.length > 0 && (
        <MapaProductos
          datos={datos}
          onSelect={nombre => {
            setBusqueda(nombre);
            setTimeout(() => {
              cardRefs.current[nombre]?.scrollIntoView({ behavior:"smooth", block:"start" });
            }, 80);
          }}
        />
      )}

      {/* Totalizador */}
      {filtrados.length > 0 && (
        <div style={{ background:"#fff", border:"0.5px solid #e8e8e6", borderRadius:12, padding:"1rem 1.25rem" }}>
          <div style={{ fontSize:11, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:"0.75rem" }}>
            Resumen · {filtrados.length} producto{filtrados.length !== 1 ? "s" : ""}
            {sinMeta > 0 && <> · <TooltipSinMeta n={sinMeta} /></>}
          </div>
          <div style={{ display:"flex", gap:"0.6rem", flexWrap:"wrap" }}>
            <div style={{ flex:1, minWidth:130, background:"#fafaf9", border:"0.5px solid #eee", borderRadius:10, padding:"0.65rem 1rem" }}>
              <div style={{ fontSize:11, color:"#888", marginBottom:3 }}>Venta total</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#1D9E75" }}>{fmt(totalVenta)}</div>
            </div>
            <div style={{ flex:1, minWidth:130, background:"#fafaf9", border:"0.5px solid #eee", borderRadius:10, padding:"0.65rem 1rem" }}>
              <div style={{ fontSize:11, color:"#888", marginBottom:3 }}>− Costo proveedor</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#E24B4A" }}>−{fmt(totalCostoProv)}</div>
            </div>
            <div style={{ flex:1, minWidth:130, background:"#fafaf9", border:"0.5px solid #eee", borderRadius:10, padding:"0.65rem 1rem" }}>
              <div style={{ fontSize:11, color:"#888", marginBottom:3 }}>− Flete entregados</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#E24B4A" }}>−{fmt(totalFleteEnt)}</div>
            </div>
            <div style={{ flex:1, minWidth:130, background:"#fafaf9", border:"0.5px solid #eee", borderRadius:10, padding:"0.65rem 1rem" }}>
              <div style={{ fontSize:11, color:"#888", marginBottom:3 }}>− Flete fallidos</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#E24B4A" }}>−{fmt(totalFleteFall)}</div>
            </div>
            <div style={{
              flex:1, minWidth:160,
              background: margenProdVend >= 0 ? "#f4fbf8" : "#FCEBEB",
              border:`0.5px solid ${margenProdVend >= 0 ? "#1D9E7530" : "#E24B4A30"}`,
              borderRadius:10, padding:"0.65rem 1rem"
            }}>
              <div style={{ fontSize:11, color:"#888", marginBottom:3 }}>Margen Productos Vendidos</div>
              <div style={{ fontSize:16, fontWeight:700, color: margenProdVend >= 0 ? "#1D9E75" : "#E24B4A" }}>{fmt(margenProdVend)}</div>
              <div style={{ fontSize:10, color:"#bbb", marginTop:2 }}>Venta − Proveedor − Fletes</div>
            </div>
          </div>
        </div>
      )}

      {/* Panel de parámetros de escenarios */}
      {datos.length > 0 && (
        <div style={{ background:"#fff", border:"0.5px solid #E4E6F5", borderRadius:12, overflow:"hidden" }}>
          <button
            onClick={() => setEscAbierto(v => !v)}
            style={{
              width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"0.75rem 1.1rem", background:"none", border:"none", cursor:"pointer",
              fontFamily:"inherit",
            }}
          >
            <span style={{ fontSize:12, fontWeight:600, color:"#4B4D6E" }}>
              ⚙️ Ajustar escenarios
            </span>
            <span style={{ fontSize:11, color:"#9496B4" }}>{escAbierto ? "▲ Cerrar" : "▼ Expandir"}</span>
          </button>
          {escAbierto && (
            <div style={{ padding:"0 1.1rem 1rem" }}>
              <SimulacionParams params={escParams} onChange={handleEscParams} />
            </div>
          )}
        </div>
      )}

      {/* Lista de productos */}
      {filtrados.length === 0 && !cargando && (
        <div style={{ color:"#aaa", fontSize:13, padding:"2rem 0", textAlign:"center" }}>
          {busqueda ? "No se encontraron productos con ese nombre." : "Sin datos para el período seleccionado."}
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
        {filtrados.map(p => (
          <div key={p.nombre} ref={el => { cardRefs.current[p.nombre] = el; }}>
            <ProductoCard p={p} escParams={escParams} />
          </div>
        ))}
      </div>

    </div>
  );
}
